// PHI: parses a census workbook's member rows (birth dates, ZIP codes,
// salaries) in the broker's browser.

import ExcelJS, { Row } from "exceljs";

import {
  BENEFIT_VALUES_MESSAGE,
  CELL_ERROR_CODE,
  COLUMNS,
  COLUMNS_BY_FORMAT,
  COVERED_VALUES_MESSAGE,
  HEADER_LOOKUP,
  PROCESS_CENSUS_FILE_RESPONSE_STATUS,
  PROCESSED_CELL_STATUS,
  TIERS_VALUES_MESSAGE,
  WAIVED_VALUES_MESSAGE,
} from "./constants";
import { COVERAGE_LABELS } from "./lib/constants";
import { getWorkbookFromFile } from "./lib/utils";
import {
  Column,
  ColumnName,
  CoverageClass,
  CustomColumn,
  CustomColumnMap,
  DataError,
  ExtendedProcessedRow,
  HeaderError,
  MissingCoveragesError,
  ProcessCensusFileResponse,
  ProcessedCell,
  ProcessedDependent,
  ProcessedRow,
  ProcessedRowWithExtras,
  ValidationFunction,
  ValueOf,
} from "./types";
import {
  findCellByColumn,
  getCounterAndLookupFromValues,
  getMissingClasses,
  getMissingError,
  getRowId,
  trimLowerCase,
} from "./utils";
import VALIDATORS, { validateValue } from "./validators";

type AggregatedCoverageClassCounterByAddress = {
  [address: string]: {
    counter: ReturnType<typeof getCounterAndLookupFromValues>[0];
    lookup: ReturnType<typeof getCounterAndLookupFromValues>[1];
    missing: Set<string>;
    address: string;
  };
};

type ColumnMap<T = ColumnName> = Record<string, Column<T>>;

type ExcludeColumn = Exclude<ColumnName, "relationship">;

type Options = {
  headerRowIndex: number;
  excludedColumns?: ExcludeColumn[];
  customColumns?: CustomColumn[];
  coverageClasses?: CoverageClass[];
  classes?: string[];
  dental?: string[];
  vision?: string[];
};

type CellWithText = Pick<ExcelJS.Cell, "text"> | { text: string };

function safeCellText(cell?: CellWithText | null): string {
  if (!cell) {
    return "";
  }

  try {
    return cell.text;
  } catch {
    return "";
  }
}

async function validateWorkbook(
  file: File,
  headerRowIndex: number,
): Promise<
  | Extract<
      ProcessCensusFileResponse,
      | { status: typeof PROCESS_CENSUS_FILE_RESPONSE_STATUS.NOT_VALID_FILE }
      | {
          status: typeof PROCESS_CENSUS_FILE_RESPONSE_STATUS.MISSING_WORKSHEET;
        }
      | {
          status: typeof PROCESS_CENSUS_FILE_RESPONSE_STATUS.MULTIPLE_WORKSHEETS;
        }
      | {
          status: typeof PROCESS_CENSUS_FILE_RESPONSE_STATUS.MISSING_HEADER_ROW;
        }
    >
  | { headerRow: Row; worksheet: ExcelJS.Worksheet }
> {
  let workbook: ExcelJS.Workbook | null = null;
  try {
    workbook = await getWorkbookFromFile(file);
  } catch {
    return {
      status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.NOT_VALID_FILE,
    };
  }

  const worksheets = workbook.worksheets;
  if (worksheets.length === 0) {
    return {
      status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.MISSING_WORKSHEET,
    };
  }

  if (worksheets.length > 1) {
    return {
      status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.MULTIPLE_WORKSHEETS,
    };
  }

  const worksheet = worksheets[0];

  const headerRow = worksheet.findRow(headerRowIndex);
  if (!headerRow) {
    return {
      status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.MISSING_HEADER_ROW,
    };
  }

  return { headerRow, worksheet };
}

async function processCensusFile(
  file: File,
  options: Options,
): Promise<ProcessCensusFileResponse> {
  const validationResult = await validateWorkbook(file, options.headerRowIndex);
  if ("status" in validationResult) {
    return validationResult;
  }

  const { headerRow, worksheet } = validationResult;

  const excludedColumns = options.excludedColumns ?? [];
  const customColumns = options.customColumns ?? [];
  const coverageClasses = options.coverageClasses ?? [];
  const classes = options.classes?.map((c) => c.toLocaleLowerCase());
  const customColumnMap = customColumns.reduce<CustomColumnMap>(
    (acc, column) => {
      acc[column.column] = column;
      return acc;
    },
    {},
  );

  const { mappedColumns, byFormatColumns, isRowFormat } = getHeaderColumnsInfo({
    headerRow,
    customColumns,
    excludedColumns,
  });

  const headerErrors = getHeaderErrors(mappedColumns);

  if (headerErrors.length > 0) {
    return {
      status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.HEADER_ERROR,
      errors: headerErrors,
    };
  }

  const firstDataRowIndex = headerRow.number + 1;
  const firstDataRow = worksheet.findRow(firstDataRowIndex);
  if (!firstDataRow) {
    return {
      status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.MISSING_DATA_ROW,
    };
  }

  const { relationship: relationshipColumn, ...otherColumns } = mappedColumns;
  const columnsWithoutRelationshipColumn = Object.values(otherColumns);

  const firstRelationshipValue = VALIDATORS[relationshipColumn.column].validate(
    safeCellText(firstDataRow.getCell(relationshipColumn.fullAddresses[0].col)),
    {},
  );
  if (firstRelationshipValue.processed !== "Employee") {
    return {
      status:
        PROCESS_CENSUS_FILE_RESPONSE_STATUS.FIRST_ROW_INVALID_RELATIONSHIP,
    };
  }

  const missingCoverageClassesError = validateCoverageClassesInWorksheet({
    coverageClasses,
    worksheet,
    headerRowNumber: headerRow.number,
  });
  if (missingCoverageClassesError) {
    return missingCoverageClassesError;
  }

  const missingClasses = new Set<string>();
  const [classesMapCounter, classesLookup] = getCounterAndLookupFromValues(
    options.classes,
  );
  const missingDental = new Set<string>();
  const [dentalMapCounter, dentalLookup] = getCounterAndLookupFromValues(
    options.dental,
  );
  const missingVision = new Set<string>();
  const [visionMapCounter, visionLookup] = getCounterAndLookupFromValues(
    options.vision,
  );
  const dataErrorMap: Partial<Record<ColumnName, DataError>> = {};
  const eligibilityClassColumn = mappedColumns[COLUMNS.ELIGIBILITY_CLASS];
  const shouldCheckClasses =
    options.classes &&
    eligibilityClassColumn &&
    eligibilityClassColumn.fullAddresses.length > 0;

  // create list of columns to identify employees
  const identifierColumnList = [
    mappedColumns[COLUMNS.GENDER],
    mappedColumns[COLUMNS.DATE_OF_BIRTH],
    mappedColumns[COLUMNS.ZIP],
  ];

  const rowContext: RowContext = {
    mappedColumns,
    byFormatColumns,
    isRowFormat,
    relationshipColumn,
    columnsWithoutRelationshipColumn,
    identifierColumnList,
    classes,
    customColumnMap,
  };

  /* the first data row has to be of type Employee or the processing will fail.
  Initializing this to empty string is ok since it is going to be updated
  to the correct row Id at the end of the row processing. */
  let lastRowEmployeeProcessedId = "";
  const processedRowByEmployeeId = new Map<string, ProcessedRowWithExtras>();
  worksheet.eachRow((row, rowNumber) => {
    // skip header row
    if (rowNumber < firstDataRowIndex) {
      return;
    }

    lastRowEmployeeProcessedId =
      processDataRow({
        row,
        rowNumber,
        context: rowContext,
        processedRowByEmployeeId,
        lastRowEmployeeProcessedId,
      }) ?? lastRowEmployeeProcessedId;
  });

  const processedRowWithExtrasList = collectEmployeeRows({
    processedRowByEmployeeId,
    relationshipColumnName: relationshipColumn.column,
    shouldCheckClasses: Boolean(shouldCheckClasses),
    classesMapCounter,
    missingClasses,
    collectErrors: internalCollectRowErrors,
  });

  const missingError = findFirstMissingError([
    {
      missing: Array.from(missingClasses.values()),
      mapCounter: classesMapCounter,
      lookup: classesLookup,
      type: "classes",
    },
    {
      missing: Array.from(missingDental.values()),
      mapCounter: dentalMapCounter,
      lookup: dentalLookup,
      type: "dental",
    },
    {
      missing: Array.from(missingVision.values()),
      mapCounter: visionMapCounter,
      lookup: visionLookup,
      type: "vision",
    },
  ]);
  if (missingError) {
    return missingError;
  }

  const dataErrors = Object.values(dataErrorMap);
  if (dataErrors.length > 0) {
    return {
      status: "data-error",
      errors: dataErrors,
    };
  }

  // check if all quoted coverages are present
  const missingCoveragesError = isRowFormat
    ? getMissingCoveragesError(processedRowWithExtrasList, excludedColumns)
    : null;
  if (missingCoveragesError) {
    return missingCoveragesError;
  }

  const data = Array.from(processedRowWithExtrasList).map((r) => ({
    ...r.row,
    rowIndexes: r.rowIndexes,
    dependents: r.dependents,
  }));

  return { status: PROCESS_CENSUS_FILE_RESPONSE_STATUS.SUCCESS, data };

  // Internal helper functions. They have access to the outer function scope.
  // Defined here for better readability taking advantage of hoisting.
  function internalCollectRowErrors(
    processedRow: ProcessedRow,
    mergedProcessedRow?: ProcessedRow,
  ): void {
    collectRowErrors({
      processedRow,
      mergedProcessedRow,
      options,
      dentalMapCounter,
      visionMapCounter,
      missingDental,
      missingVision,
      dataErrorMap,
    });
  }
}

type RowContext = {
  mappedColumns: ColumnMap;
  byFormatColumns: ColumnMap;
  isRowFormat: boolean;
  relationshipColumn: Column<ColumnName>;
  columnsWithoutRelationshipColumn: Column<ColumnName>[];
  identifierColumnList: Column<ColumnName>[];
  classes?: string[];
  customColumnMap: CustomColumnMap;
};

type ValidatedRow = {
  processedRow: ProcessedRow;
  employeeId: string | null;
};

function validateColumns<T extends string>(
  columns: Column<T>[],
  row: Row,
  processedRow: ProcessedRow,
  context: RowContext,
): void {
  for (const column of columns) {
    validateColumn({
      column,
      row,
      processedRow,
      mappedColumns: context.mappedColumns,
      classes: context.classes,
      customColumnMap: context.customColumnMap,
    });
  }
}

/**
 * Validates the relationship column and conditionally validates other columns.
 *
 * This function first validates the relationship column for the given row. If the
 * relationship is invalid or missing, it returns false to indicate the row should
 * be skipped. If the relationship is valid and the value is "Employee", it validates
 * all other columns. For non-Employee relationships (e.g., Spouse, Child), only the
 * relationship column is validated, allowing partial row data for dependents.
 *
 * @param row - The Excel row to validate
 * @param processedRow - The processed row object that will be populated with validation results
 * @returns true if the row should be processed further, false if it should be skipped
 */
function checkRelationshipAndValidateColumns(
  row: Row,
  processedRow: ProcessedRow,
  context: RowContext,
): boolean {
  const { relationshipColumn, columnsWithoutRelationshipColumn } = context;
  validateColumns([relationshipColumn], row, processedRow, context);
  const processedRelationship = processedRow[relationshipColumn.column];
  // if the relationship is not valid, skip the row
  if (!processedRelationship) {
    return false;
  }
  if (processedRelationship.processed === "Employee") {
    validateColumns(
      columnsWithoutRelationshipColumn,
      row,
      processedRow,
      context,
    );
  }
  return true;
}

function validateColumnFormatRow(
  row: Row,
  context: RowContext,
): ValidatedRow | null {
  const processedRow: ProcessedRow = {};
  return checkRelationshipAndValidateColumns(row, processedRow, context)
    ? { processedRow, employeeId: null }
    : null;
}

function validateRowFormatRow({
  row,
  rowNumber,
  context,
  processedRowByEmployeeId,
}: {
  row: Row;
  rowNumber: number;
  context: RowContext;
  processedRowByEmployeeId: Map<string, ProcessedRowWithExtras>;
}): ValidatedRow | null {
  const processedRow: ProcessedRow = {};
  validateColumns(context.identifierColumnList, row, processedRow, context);
  const employeeId = getRowId(processedRow);

  const previousRowWithExtras = employeeId
    ? processedRowByEmployeeId.get(employeeId)
    : null;
  let rowToKeep = processedRow;
  if (previousRowWithExtras) {
    validateColumns(
      context.columnsWithoutRelationshipColumn,
      row,
      processedRow,
      context,
    );
    mergeProcessedRows(previousRowWithExtras.row, processedRow);
    previousRowWithExtras.rowIndexes?.push(rowNumber);
    previousRowWithExtras.associatedRows?.push(processedRow);
    rowToKeep = previousRowWithExtras.row;
  } else if (!checkRelationshipAndValidateColumns(row, processedRow, context)) {
    return null;
  }

  // if benefit is not valid value and error code is not defined ignore the cell by deleting it.
  if (rowToKeep.benefit?.status !== "valid" && !rowToKeep.benefit?.errorCode) {
    delete rowToKeep.benefit;
  }
  return { processedRow: rowToKeep, employeeId };
}

function addDependentToLastEmployee({
  row,
  processedRow,
  context,
  processedRowByEmployeeId,
  lastRowEmployeeProcessedId,
}: {
  row: Row;
  processedRow: ProcessedRow;
  context: RowContext;
  processedRowByEmployeeId: Map<string, ProcessedRowWithExtras>;
  lastRowEmployeeProcessedId: string;
}): void {
  const processedRelationship = processedRow[context.relationshipColumn.column];
  const isDependent =
    processedRelationship?.status === "valid" &&
    processedRelationship.processed !== "Employee";
  if (!isDependent) {
    return;
  }

  validateColumns(
    [
      context.mappedColumns[COLUMNS.GENDER],
      context.mappedColumns[COLUMNS.DATE_OF_BIRTH],
    ],
    row,
    processedRow,
    context,
  );
  processedRowByEmployeeId
    .get(lastRowEmployeeProcessedId)
    ?.dependents?.push(processedRow as ProcessedDependent);
}

// in row format, valid benefit columns are not needed in the final result
function splitBenefitColumns(
  processedRow: ProcessedRow,
  context: RowContext,
): { row: ExtendedProcessedRow; areBenefitsValid: boolean } {
  if (!context.isRowFormat) {
    return { row: processedRow, areBenefitsValid: true };
  }

  const entries = Object.entries(processedRow);
  const isBenefitColumn = ([key]: [string, ProcessedCell]): boolean =>
    context.byFormatColumns[key] !== undefined;
  return {
    row: Object.fromEntries(entries.filter((entry) => !isBenefitColumn(entry))),
    areBenefitsValid: entries
      .filter(isBenefitColumn)
      .every(([, value]) => value.status === "valid"),
  };
}

/**
 * Validates one data row and stores it in `processedRowByEmployeeId`.
 *
 * @returns the id the row was stored under, or null if the row was skipped
 */
function processDataRow({
  row,
  rowNumber,
  context,
  processedRowByEmployeeId,
  lastRowEmployeeProcessedId,
}: {
  row: Row;
  rowNumber: number;
  context: RowContext;
  processedRowByEmployeeId: Map<string, ProcessedRowWithExtras>;
  lastRowEmployeeProcessedId: string;
}): string | null {
  const validatedRow = context.isRowFormat
    ? validateRowFormatRow({
        row,
        rowNumber,
        context,
        processedRowByEmployeeId,
      })
    : validateColumnFormatRow(row, context);
  if (!validatedRow) {
    return null;
  }

  const { processedRow, employeeId } = validatedRow;
  addDependentToLastEmployee({
    row,
    processedRow,
    context,
    processedRowByEmployeeId,
    lastRowEmployeeProcessedId,
  });

  const employeeKey = employeeId ?? String(rowNumber);
  const processedRowWithExtras = processedRowByEmployeeId.get(employeeKey);
  const { row: rowToSave, areBenefitsValid } = splitBenefitColumns(
    processedRow,
    context,
  );
  processedRowByEmployeeId.set(employeeKey, {
    row: rowToSave,
    rowIndexes: processedRowWithExtras?.rowIndexes || [rowNumber],
    dependents: processedRowWithExtras?.dependents || [],
    associatedRows: processedRowWithExtras?.associatedRows || [processedRow],
    areBenefitsValidForRowFormat:
      (processedRowWithExtras?.areBenefitsValidForRowFormat ?? true) &&
      areBenefitsValid,
  });
  return employeeKey;
}

function collectEmployeeRows({
  processedRowByEmployeeId,
  relationshipColumnName,
  shouldCheckClasses,
  classesMapCounter,
  missingClasses,
  collectErrors,
}: {
  processedRowByEmployeeId: Map<string, ProcessedRowWithExtras>;
  relationshipColumnName: ColumnName;
  shouldCheckClasses: boolean;
  classesMapCounter: Record<string, number>;
  missingClasses: Set<string>;
  collectErrors: (
    processedRow: ProcessedRow,
    mergedProcessedRow?: ProcessedRow,
  ) => void;
}): ProcessedRowWithExtras[] {
  const employeeRows: ProcessedRowWithExtras[] = [];
  for (const employeeRow of processedRowByEmployeeId.values()) {
    const processedRow = employeeRow.row;

    if (employeeRow.areBenefitsValidForRowFormat) {
      collectErrors(processedRow);
    } else {
      employeeRow.associatedRows?.forEach((r) =>
        collectErrors(r, processedRow),
      );
    }

    if (processedRow[relationshipColumnName]?.processed !== "Employee") {
      continue;
    }

    if (shouldCheckClasses) {
      registerClassCounterComponents({
        value: processedRow[COLUMNS.ELIGIBILITY_CLASS]?.original,
        counter: classesMapCounter,
        missing: missingClasses,
      });
    }

    employeeRows.push(employeeRow);
  }
  return employeeRows;
}

function findFirstMissingError(
  checks: Parameters<typeof getMissingError>[0][],
): ReturnType<typeof getMissingError> {
  for (const check of checks) {
    const error = getMissingError(check);
    if (error) {
      return error;
    }
  }
  return null;
}

function getMissingCoveragesError(
  rows: ProcessedRowWithExtras[],
  excludedColumns: ColumnName[],
): Extract<ProcessCensusFileResponse, { status: "missing-coverages" }> | null {
  const requiredColumns = COLUMNS_BY_FORMAT.COLUMN.filter(
    (c) => !excludedColumns.includes(c),
  );
  const errors: MissingCoveragesError[] = rows.flatMap((processedRow) => {
    const rowColumns = Object.keys(processedRow.row);
    const missingColumns = requiredColumns.filter(
      (c) => !rowColumns.includes(c),
    );
    return missingColumns.length > 0
      ? [
          {
            rowIndexes: processedRow.rowIndexes ?? [],
            coverages: missingColumns,
          },
        ]
      : [];
  });
  return errors.length > 0 ? { status: "missing-coverages", errors } : null;
}

function getHeaderError<T extends string>(
  header: Column<T>,
  reason: HeaderError["reason"],
): HeaderError {
  const { column, location, values } = header;
  return { column, location, values, reason };
}

function getHeaderErrors<T extends string>(
  columns: ColumnMap<T>,
): HeaderError[] {
  return Object.values(columns).reduce<HeaderError[]>((errors, column) => {
    if (column.skipHeaderValidation) {
      return errors;
    } else if (column.values.length > 1) {
      errors.push(getHeaderError(column, "duplicate"));
    } else if (column.values.length === 0) {
      errors.push(getHeaderError(column, "missing"));
    }
    return errors;
  }, []);
}

function validateColumn<T extends string>({
  column,
  row,
  processedRow,
  mappedColumns,
  classes,
  customColumnMap,
}: {
  column: Column<T>;
  row: Row;
  processedRow: ExtendedProcessedRow;
  mappedColumns: ColumnMap;
  classes?: string[];
  customColumnMap: CustomColumnMap;
}): void {
  // return early if the column is already processed
  if (processedRow[column.column]) {
    return;
  }

  const cell = column.getCustomValue
    ? column.getCustomValue(findCellByColumn(column, row), processedRow)
    : row.getCell(column.fullAddresses[0].col);

  let processedValue: ProcessedCell;
  if (typeof cell.address === "string") {
    let cellText = safeCellText(cell);

    if (cell.type === ExcelJS.ValueType.Date) {
      cellText = new Date(cellText).toLocaleDateString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        timeZone: "UTC",
      });
    }
    for (const dependency of column.dependencies) {
      const dependencyColumn = mappedColumns[dependency];
      if (dependencyColumn) {
        validateColumn({
          column: dependencyColumn,
          row,
          processedRow,
          mappedColumns,
          customColumnMap,
        });
      }
    }

    processedValue = getValidatorForColumn(column.column)(
      cellText,
      processedRow,
      {
        classes,
        row,
        cell,
        customColumnMap,
      },
    );
    processedValue.cellAddress = cell.address;
  } else {
    const originalCell = findCellByColumn(column, row);
    processedValue = {
      status: "valid",
      original: safeCellText(originalCell),
      processed: safeCellText(cell),
    };
  }

  processedRow[column.column] = processedValue;
}

const WAIVED_OR_TIERS_MESSAGE = `Expected ${WAIVED_VALUES_MESSAGE}, ${TIERS_VALUES_MESSAGE}`;
const WAIVED_OR_COVERED_MESSAGE = `Expected ${WAIVED_VALUES_MESSAGE}, ${COVERED_VALUES_MESSAGE}`;
const PLAN_NAME_MAPPING_MESSAGE = "Expected a value mapped to a plan name";
const EXPECTED_VALUE_MESSAGE = "Expected a value";
const WAIVED_OR_NUMBER_MESSAGE = `Expected a valid number or ${WAIVED_VALUES_MESSAGE}`;

const ERROR_MESSAGE_BY_COLUMN: Partial<Record<ColumnName, string>> = {
  relationship:
    "Expected values: 'Employee', 'EE', 'Self', 'Member', 'Spouse', 'SP', 'Domestic Partner', 'DP', 'Partner', 'Child', 'Children', 'CH', 'Dependent', 'Legal Dependent'",
  gender:
    "Expected values: 'Male', 'M', 'Female', 'F', 'Not Disclosed', 'Not Specified', 'Non Binary', 'Non-Binary', 'NB'",
  "date of birth":
    "Expected a value with any of the following formats: 'MM/DD/YYYY', 'M/D/YYYY', 'M/D/YY', 'YYYY/MM/DD', 'YYYY/M/D', 'YYYY/MM/D', 'YYYY/M/DD'",
  zip: "Expected a value with format '#####' or '#####-####'",
  "employment status":
    "Expected values: 'Active', 'A', 'A - Active', 'Retired', 'Retire', 'R', 'R- Retiree', 'Retiree', 'Cobra', 'C', 'C - COBRA', 'Leave', 'On Leave', 'L', 'L - Leave', 'Terminated', 'Term', 'T', 'T - Terminated'",
  "annual salary": "Expected number greater than 0",
  "dental plan name": PLAN_NAME_MAPPING_MESSAGE,
  "vision plan name": PLAN_NAME_MAPPING_MESSAGE,
  [COLUMNS.EMPLOYMENT_TYPE]: EXPECTED_VALUE_MESSAGE,
  occupation: EXPECTED_VALUE_MESSAGE,
  "eligibility class": EXPECTED_VALUE_MESSAGE,
  [COLUMNS.DENTAL_PLAN_COVERAGE]: WAIVED_OR_TIERS_MESSAGE,
  [COLUMNS.VISION_PLAN_COVERAGE]: WAIVED_OR_TIERS_MESSAGE,
  [COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE]: WAIVED_OR_COVERED_MESSAGE,
  [COLUMNS.STD_ELECTION]: WAIVED_OR_COVERED_MESSAGE,
  [COLUMNS.LTD_ELECTION]: WAIVED_OR_COVERED_MESSAGE,
  [COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE]: WAIVED_OR_NUMBER_MESSAGE,
  [COLUMNS.VOLUNTARY_ADD_EE_COVERAGE]: WAIVED_OR_NUMBER_MESSAGE,
  [COLUMNS.PLAN_NAME]: `Expected values: ${BENEFIT_VALUES_MESSAGE}`,
};

const BENEFIT_ERROR_MESSAGE_BY_CODE: Record<string, string> = {
  "unmapped-life":
    "A benefit called 'Life' has been detected, rename to Voluntary Life or Basic Life. Alternatively, include a column for Plan Name to indicate which type of Life coverage",
  "multiple-benefits": "Multiple benefits have been detected",
};

function getBenefitErrorMessage(processedRow: ProcessedRow): string {
  const errorCode = processedRow[COLUMNS.BENEFIT]?.errorCode;
  if (!errorCode) {
    return "Expected a valid coverage";
  }
  const message = BENEFIT_ERROR_MESSAGE_BY_CODE[errorCode];
  if (message === undefined) {
    throw new Error(`Unknown benefit error code: ${errorCode}`);
  }
  return message;
}

function getErrorMessage(
  column: ColumnName,
  processedRow: ProcessedRow,
): string {
  if (processedRow[column]?.errorCode === CELL_ERROR_CODE.MULTIPLE_VALUES) {
    return "The value does not match the value provided for the same column in another row.";
  }

  if (column === COLUMNS.BENEFIT) {
    return getBenefitErrorMessage(processedRow);
  }

  if (column === COLUMNS.COVERAGE_TIER) {
    const benefit = processedRow[COLUMNS.BENEFIT]?.processed;
    const benefitColumn = benefit as ColumnName;
    return getErrorMessage(benefitColumn, processedRow);
  }

  const message = ERROR_MESSAGE_BY_COLUMN[column];
  if (message === undefined) {
    throw new Error(`Invalid error message for column ${column}`);
  }
  return message;
}

function getValidatorForColumn(column: string): ValidationFunction {
  const validator = VALIDATORS[column as ColumnName]; // it's safe to cast since we are checking that validator exists
  if (validator) {
    return validator.validate;
  }
  // return validator that always returns valid
  return validateValue;
}

function initColumn<T>(column: T): Omit<Column<T>, "dependencies"> {
  return {
    column,
    location: [],
    values: [],
    fullAddresses: [],
  };
}

function initColumns<T extends string>(
  columns: T[],
  getDependencies: (column: T) => T[],
): ColumnMap<T> {
  return columns.reduce<ColumnMap<T>>((acc, column) => {
    acc[column] = {
      ...initColumn(column),
      dependencies: getDependencies(column),
    };
    return acc;
  }, {});
}

function validateCoverageClassesInWorksheet({
  coverageClasses,
  worksheet,
  headerRowNumber,
}: {
  coverageClasses: CoverageClass[];
  worksheet: ExcelJS.Worksheet;
  headerRowNumber: number;
}): Extract<ProcessCensusFileResponse, { status: "mapping-error" }> | null {
  // The same column address can be used for multiple coverage classes.
  // To avoid duplicating the counter and missing sets, we group by address.
  const aggregatedCoverageClassCounterByAddress: AggregatedCoverageClassCounterByAddress =
    {};
  for (const coverageClass of coverageClasses) {
    const address = coverageClass.column;
    const [counter, lookup] = getCounterAndLookupFromValues(
      coverageClass.classes,
    );
    const coverageClassCounter =
      aggregatedCoverageClassCounterByAddress[address];
    if (coverageClassCounter) {
      Object.assign(coverageClassCounter.counter, counter);
      Object.assign(coverageClassCounter.lookup, lookup);
    } else {
      aggregatedCoverageClassCounterByAddress[address] = {
        missing: new Set<string>(),
        counter,
        lookup,
        address,
      };
    }
  }
  // check if the coverage class columns have values that are not in the provided classes for each coverage class column
  coverageClasses.forEach((coverageClass) => {
    const excelColumn = worksheet.getColumn(coverageClass.column);
    const coverageClassCounter =
      aggregatedCoverageClassCounterByAddress[coverageClass.column];
    excelColumn.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      // skip header row
      if (rowNumber === headerRowNumber) {
        return;
      }
      registerClassCounterComponents({
        value: safeCellText(cell),
        counter: coverageClassCounter.counter,
        missing: coverageClassCounter.missing,
      });
    });
  });

  return getCoverageClassesMissingError({
    coverageClasses,
    aggregatedCoverageClassCounterByAddress,
  });
}

function registerClassCounterComponents({
  value,
  counter,
  missing,
}: {
  value?: string;
  counter: Record<string, number>;
  missing: Set<string>;
}): void {
  // skip empty string
  if (!value) {
    return;
  }
  const lowerCaseText = value.toLocaleLowerCase();
  if (counter[lowerCaseText] !== undefined) {
    counter[lowerCaseText]++;
  } else {
    missing.add(value);
  }
}

function getHeaderColumnsInfo({
  headerRow,
  customColumns,
  excludedColumns,
}: {
  headerRow: Row;
  customColumns: CustomColumn[];
  excludedColumns: ColumnName[];
}): {
  byFormatColumns: ColumnMap;
  mappedColumns: ColumnMap;
  isRowFormat: boolean;
} {
  const requiredColumnNames = Object.values(COLUMNS).filter(
    (column) => !excludedColumns.includes(column),
  );
  const columns = initColumns(
    requiredColumnNames,
    (column) => VALIDATORS[column].dependencies ?? [],
  );

  // create custom columns provided by the caller
  for (const customColumn of customColumns) {
    columns[customColumn.column] = {
      ...initColumn(customColumn.column),
      ...customColumn,
      dependencies: customColumn.dependencies || [],
    };
  }

  headerRow.eachCell((cell) => {
    const cellText = safeCellText(cell);
    const headerColumn: Column<ColumnName | string> | undefined =
      columns[HEADER_LOOKUP[trimLowerCase(cellText)]];

    if (headerColumn) {
      headerColumn.location.push(cell.address);
      headerColumn.values.push(cell.value);
      headerColumn.fullAddresses.push(cell.fullAddress);
    }
  });

  const commonColumns = filterColumns(columns, COLUMNS_BY_FORMAT.COMMON);
  const isRowFormat = checkIfRowFormat(columns);
  const byFormatColumns = filterColumns(
    columns,
    isRowFormat ? COLUMNS_BY_FORMAT.ROW : COLUMNS_BY_FORMAT.COLUMN,
  );

  const mappedColumns = { ...commonColumns, ...byFormatColumns };

  return {
    mappedColumns,
    byFormatColumns,
    isRowFormat,
  };
}

function filterColumns(
  headerColumns: ColumnMap,
  columns: ValueOf<typeof COLUMNS>[],
): ColumnMap {
  return columns.reduce<ColumnMap>((acc, column) => {
    const headerColumn = headerColumns[column];
    if (headerColumn) {
      acc[column] = headerColumn;
    }
    return acc;
  }, {});
}

function checkIfRowFormat(columns: ColumnMap): boolean {
  const benefitColumn = columns[COLUMNS.BENEFIT];
  const coverageTierColumn = columns[COLUMNS.COVERAGE_TIER];
  return Boolean(
    benefitColumn &&
    coverageTierColumn &&
    benefitColumn.values.length > 0 &&
    coverageTierColumn.values.length > 0,
  );
}

function updateCounterAndMissing(
  value: ProcessedCell,
  mapCounter: Record<string, number>,
  missingSet: Set<string>,
): void {
  if (value.status === "valid") {
    if (value.original) {
      const lowerCaseText = value.original.toLocaleLowerCase();
      if (mapCounter[lowerCaseText] !== undefined) {
        mapCounter[lowerCaseText]++;
      }
    }
  } else {
    missingSet.add(value.original);
  }
}

const mergeProcessedRows = (
  row1: ExtendedProcessedRow,
  row2: ExtendedProcessedRow,
): void => {
  for (const [key, value2] of Object.entries(row2)) {
    if (value2.status !== "valid") {
      continue;
    }

    const value1 = row1[key];

    if (!value1) {
      row1[key] = value2;
      continue;
    }

    if (value1.errorCode === CELL_ERROR_CODE.MULTIPLE_VALUES) {
      continue;
    }

    if (value1.status !== "valid") {
      row1[key] = value2;
      continue;
    }

    // Check for conflict: both valid but different processed values
    if (value1.processed !== value2.processed) {
      value1.status = PROCESSED_CELL_STATUS.INVALID_VALUE;
      value1.errorCode = "multiple-values";
      row1[key] = value1;

      value2.status = PROCESSED_CELL_STATUS.INVALID_VALUE;
      value2.errorCode = "multiple-values";
      row2[key] = value2;
    }
  }
};

function collectRowErrors({
  processedRow,
  mergedProcessedRow,
  options,
  dentalMapCounter,
  visionMapCounter,
  missingDental,
  missingVision,
  dataErrorMap,
}: {
  processedRow: ProcessedRow;
  mergedProcessedRow?: ProcessedRow;
  options: Options;
  dentalMapCounter: Record<string, number>;
  visionMapCounter: Record<string, number>;
  missingDental: Set<string>;
  missingVision: Set<string>;
  dataErrorMap: Partial<Record<ColumnName, DataError>>;
}): void {
  for (const [key, value] of Object.entries(processedRow)) {
    if (key === "dental plan name" && options.dental) {
      updateCounterAndMissing(value, dentalMapCounter, missingDental);
    } else if (key === "vision plan name" && options.vision) {
      updateCounterAndMissing(value, visionMapCounter, missingVision);
    }

    const column = key as ColumnName;

    if (value.status === "valid" || !value.cellAddress) {
      continue;
    }

    if (mergedProcessedRow) {
      const mergedValue = mergedProcessedRow[column];
      // skip if the merged value is valid and original value is empty
      if (
        mergedValue &&
        mergedValue.status === "valid" &&
        value.original === ""
      ) {
        continue;
      }
    }

    const dataError: DataError = dataErrorMap[column] ?? {
      column,
      location: [],
      values: [],
      messages: [],
    };

    dataError.messages.push(
      `Invalid value. ${getErrorMessage(column, processedRow)}`,
    );
    dataError.location.push(value.cellAddress);
    dataError.values.push(value.original);
    dataErrorMap[column] = dataError;
  }
}

function getCoverageClassesMissingError({
  coverageClasses,
  aggregatedCoverageClassCounterByAddress,
}: {
  coverageClasses: CoverageClass[];
  aggregatedCoverageClassCounterByAddress: AggregatedCoverageClassCounterByAddress;
}): Extract<ProcessCensusFileResponse, { status: "mapping-error" }> | null {
  if (coverageClasses.length === 0) {
    return null;
  }

  const missingInCensus: string[] = [];
  for (const coverageClass of coverageClasses) {
    const address = coverageClass.column;
    const coverageClassCounter =
      aggregatedCoverageClassCounterByAddress[address];
    if (!coverageClassCounter) {
      // No counter data was collected for this address; skip to next coverage class.
      continue;
    }

    // Collect counter data for this coverage classes.
    const counter: Record<string, number> = {};
    for (const className of coverageClass.classes) {
      const lowerCaseClassName = className.toLocaleLowerCase();
      counter[lowerCaseClassName] =
        coverageClassCounter.counter[lowerCaseClassName] ?? 0;
    }

    const missingClasses = getMissingClasses(
      counter,
      coverageClassCounter.lookup,
    );
    if (missingClasses.length === 0) {
      continue;
    }

    missingInCensus.push(
      `${COVERAGE_LABELS[coverageClass.coverage] ?? coverageClass.coverage}: ${missingClasses.join(", ")}`,
    );
  }

  const missingInForm: string[] = [];
  for (const cc of Object.values(aggregatedCoverageClassCounterByAddress)) {
    const missing = Array.from(cc.missing);
    if (missing.length === 0) {
      continue;
    }
    missingInForm.push(`${cc.address}: ${missing.join(", ")}`);
  }

  if (missingInCensus.length === 0 && missingInForm.length === 0) {
    return null;
  }

  return {
    type: "coverage-classes",
    status: "mapping-error",
    missing: {
      form: missingInForm,
      census: missingInCensus,
    },
  };
}

export default processCensusFile;
export { ExcludeColumn, Options, processCensusFile };
